import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import CompanyRegisterForm from "../../components/auth/CompanyRegisterForm";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import { MODULE_REGISTRY } from "../registry";
import "./AuthPages.css";

const PRO_BASE_PRICE_PER_USER_MONTH = 499;
const PRO_DURATION_OPTIONS = [
  { key: "1m", label: "1 Month", months: 1, discountPercent: 0 },
  { key: "3m", label: "3 Months", months: 3, discountPercent: 5 },
  { key: "6m", label: "6 Months", months: 6, discountPercent: 10 },
  { key: "12m", label: "1 Year", months: 12, discountPercent: 15 },
];

const MODULE_ICONS = {
  files: "🗂",
  factory: "🏭",
};

function formatInr(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Number(value) || 0));
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.querySelector('script[data-razorpay="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Razorpay SDK")),
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.dataset.razorpay = "true";
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.body.appendChild(script);
  });
}

function ModuleOptionCard({ label, description, icon, selected, onSelect }) {
  const iconChar = MODULE_ICONS[icon] || "●";
  return (
    <button
      type="button"
      className={`register-plan-card register-module-card ${selected ? "selected" : ""}`}
      onClick={onSelect}
    >
      <div className="register-plan-title-row">
        <strong>
          <span className="register-module-icon">{iconChar}</span>
          {label}
        </strong>
        {selected && (
          <span className="register-plan-badge">Selected</span>
        )}
      </div>
      <p>{description}</p>
    </button>
  );
}

export default function CompanyRegisterPage() {
  const navigate = useNavigate();
  const { setAccessToken, updateUser } = useAuth();

  const [step, setStep] = useState(1);

  const [selectedModule, setSelectedModule] = useState(null);

  const [selectedPlan, setSelectedPlan] = useState("PRO");
  const [userCount, setUserCount] = useState(10);
  const [proDurationKey, setProDurationKey] = useState("1m");
  const [durationDiscounts, setDurationDiscounts] = useState(() =>
    PRO_DURATION_OPTIONS.reduce((acc, item) => {
      acc[item.months] = item.discountPercent;
      return acc;
    }, {}),
  );

  const [formData, setFormData] = useState({
    companyName: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedDuration =
    PRO_DURATION_OPTIONS.find((item) => item.key === proDurationKey) ||
    PRO_DURATION_OPTIONS[0];

  useEffect(() => {
    let active = true;
    const loadPricing = async () => {
      try {
        const resp = await api.get("/tenant/pricing");
        const map = resp?.data?.discountMap?.pro || {};
        if (!active || !map || typeof map !== "object") return;
        setDurationDiscounts((prev) => {
          const next = { ...prev };
          Object.keys(map).forEach((months) => {
            const numericMonths = Number(months);
            const numericDiscount = Number(map[months]);
            if (
              Number.isFinite(numericMonths) &&
              Number.isFinite(numericDiscount)
            ) {
              next[numericMonths] = numericDiscount;
            }
          });
          return next;
        });
      } catch (err) {
        // Fallback to defaults when pricing config is unavailable.
      }
    };
    loadPricing();
    return () => {
      active = false;
    };
  }, []);

  const pricing = useMemo(() => {
    const safeUsers = Math.max(1, Number(userCount) || 1);
    const effectiveDiscount =
      durationDiscounts[selectedDuration.months] ??
      selectedDuration.discountPercent;
    const gross =
      safeUsers * PRO_BASE_PRICE_PER_USER_MONTH * selectedDuration.months;
    const discountAmount = Math.round((gross * effectiveDiscount) / 100);
    const total = gross - discountAmount;
    const perUserForTerm = total / safeUsers;
    return {
      users: safeUsers,
      gross,
      discountAmount,
      total,
      perUserForTerm,
      effectiveDiscount,
    };
  }, [userCount, selectedDuration, durationDiscounts]);

  const actionButtonLabel =
    selectedPlan === "TRIAL"
      ? "Start Free Trial"
      : selectedPlan === "PRO"
        ? "Proceed to Payment"
        : "Contact Sales";

  const validateFormStep = () => {
    if (!formData.companyName.trim()) return "Company name is required";
    if (!formData.adminFirstName.trim()) return "First name is required";
    if (!formData.adminLastName.trim()) return "Last name is required";
    if (!formData.adminEmail.trim()) return "Work email is required";
    if (!formData.adminPassword) return "Password is required";
    if (formData.adminPassword.length < 8)
      return "Password must be at least 8 characters";
    if (formData.adminPassword !== formData.confirmPassword)
      return "Passwords do not match";
    return null;
  };

  const validatePlanStep = () => {
    if (selectedPlan === "TRIAL" && pricing.users > 10)
      return "Trial allows up to 10 users only";
    return null;
  };

  const handleNextStep = () => {
    const message = validateFormStep();
    if (message) {
      setError(message);
      return;
    }
    setError("");
    setStep(2);
  };

  const handleModuleNext = () => {
    if (!selectedModule) {
      setError("Please select a module to continue");
      return;
    }
    setError("");
    setStep(3);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    handleNextStep();
  };

  const completeRegistration = async (paymentPayload = null) => {
    const moduleEntry = MODULE_REGISTRY[selectedModule];

    const registerPayload = {
      companyName: formData.companyName,
      adminFirstName: formData.adminFirstName,
      adminLastName: formData.adminLastName,
      adminEmail: formData.adminEmail,
      adminPassword: formData.adminPassword,
      planType: selectedPlan,
      planDurationMonths: selectedPlan === "PRO" ? selectedDuration.months : 0,
      totalUsersExpected: pricing.users,
      module: selectedModule,
      payment: paymentPayload || undefined,
    };

    const resp = await api.post("/tenant/register", registerPayload);
    const { accessToken, refreshToken, user } = resp.data;

    setAccessToken(accessToken);
    localStorage.setItem("accessToken", accessToken);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    updateUser(user);
    navigate(moduleEntry?.defaultRoute || "/");
  };

  const openRazorpayCheckout = ({ order, keyId }) =>
    new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        name: formData.companyName || "DecisionFlow",
        description: `Pro Plan - ${selectedDuration.label}`,
        order_id: order.id,
        handler: (response) => {
          resolve({
            provider: "RAZORPAY",
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          });
        },
        prefill: {
          name: `${formData.adminFirstName} ${formData.adminLastName}`.trim(),
          email: formData.adminEmail,
        },
        notes: {
          planType: "PRO",
          totalUsers: String(pricing.users),
          durationMonths: String(selectedDuration.months),
        },
        theme: {
          color: "#0f62c7",
        },
        modal: {
          ondismiss: () => reject(new Error("Payment was cancelled")),
        },
      });

      rzp.open();
    });

  const handlePrimaryAction = async () => {
    const message = validatePlanStep();
    if (message) {
      setError(message);
      return;
    }

    if (selectedPlan === "ENTERPRISE") {
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (selectedPlan === "TRIAL") {
        await completeRegistration(null);
        return;
      }

      await loadRazorpayScript();
      const orderResp = await api.post("/tenant/register/order", {
        companyName: formData.companyName,
        adminEmail: formData.adminEmail,
        totalUsers: pricing.users,
        durationMonths: selectedDuration.months,
      });

      const paymentPayload = await openRazorpayCheckout({
        order: {
          id: orderResp.data.orderId,
          amount: orderResp.data.amount,
          currency: orderResp.data.currency,
        },
        keyId: orderResp.data.keyId,
      });

      await completeRegistration(paymentPayload);
    } catch (err) {
      setError(
        err.response?.data?.error || err.response?.data?.message || err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const modules = Object.values(MODULE_REGISTRY);

  return (
    <div className="auth-shell">
      <section className="auth-main">
        <div
          className="auth-container"
          style={{ maxWidth: step === 3 ? 600 : 520 }}
        >
          <div className="register-step-indicator">
            <div
              className={`register-step-dot ${step >= 1 ? "active" : ""} ${step > 1 ? "done" : ""}`}
            >
              <span>1</span>
            </div>
            <div className={`register-step-line ${step > 1 ? "done" : ""}`} />
            <div
              className={`register-step-dot ${step >= 2 ? "active" : ""} ${step > 2 ? "done" : ""}`}
            >
              <span>2</span>
            </div>
            <div className={`register-step-line ${step > 2 ? "done" : ""}`} />
            <div className={`register-step-dot ${step >= 3 ? "active" : ""}`}>
              <span>3</span>
            </div>
          </div>

          {step === 1 && (
            <>
              <div className="auth-header">
                <h1>Create Your Company Account</h1>
                <p>
                  Set up your workspace and administrator account to start
                  managing workflows.
                </p>
              </div>
              <CompanyRegisterForm
                formId="company-register-form"
                hideSubmitButton
                data={formData}
                error={error}
                loading={loading}
                onSubmit={handleFormSubmit}
                onChange={(name, value) => {
                  setFormData((prev) => ({ ...prev, [name]: value }));
                }}
              />
              <button
                type="button"
                className="auth-btn auth-btn-primary"
                style={{ marginTop: 16, width: "100%" }}
                onClick={handleNextStep}
              >
                Continue to Module Selection &rarr;
              </button>
              <div className="auth-links">
                <span>Already have an account?</span>
                <Link to="/login">Log in</Link>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="auth-header">
                <h1>Choose Your Module</h1>
                <p>
                  Select the business module that best fits how your team works.
                  This determines the core features of your workspace.
                </p>
              </div>

              <div className="register-module-grid" style={{ marginTop: 16 }}>
                {modules.map((mod) => (
                  <ModuleOptionCard
                    key={mod.id}
                    label={mod.label}
                    description={mod.description}
                    icon={mod.icon}
                    selected={selectedModule === mod.id}
                    onSelect={() => {
                      setSelectedModule(mod.id);
                      setError("");
                    }}
                  />
                ))}
              </div>

              {error && (
                <div className="auth-alert" style={{ marginTop: 12 }}>
                  {error}
                </div>
              )}

              <div className="register-step2-actions" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="auth-btn auth-btn-secondary"
                  onClick={() => {
                    setError("");
                    setStep(1);
                  }}
                >
                  &larr; Back
                </button>
                <button
                  type="button"
                  className="auth-btn auth-btn-primary"
                  disabled={!selectedModule}
                  onClick={handleModuleNext}
                >
                  Continue to Plan Selection &rarr;
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="auth-header">
                <h1>Choose Your Plan</h1>
                <p>
                  Select the plan and billing period that works best for your
                  team.
                </p>
              </div>

              <div className="register-pricing-panel" style={{ marginTop: 16 }}>
                <div className="register-user-count-row">
                  <label htmlFor="plan-users">Total Users</label>
                  <input
                    id="plan-users"
                    type="number"
                    min={1}
                    className="auth-input"
                    value={userCount}
                    onChange={(e) => setUserCount(e.target.value)}
                  />
                </div>

                <div className="register-plan-grid">
                  <button
                    type="button"
                    className={`register-plan-card ${selectedPlan === "TRIAL" ? "selected" : ""}`}
                    onClick={() => setSelectedPlan("TRIAL")}
                  >
                    <div className="register-plan-title-row">
                      <strong>Trial</strong>
                      <span className="register-plan-badge">14 Days</span>
                    </div>
                    <p>Free trial for up to 10 users for 2 weeks.</p>
                    <div className="register-plan-price">{formatInr(0)}</div>
                    <div className="register-plan-foot">
                      {pricing.users > 10
                        ? "Current user count is above trial limit."
                        : `${pricing.users}/10 users in trial limit`}
                    </div>
                  </button>

                  <div
                    className={`register-plan-card register-plan-card-pro ${selectedPlan === "PRO" ? "selected" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedPlan("PRO")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSelectedPlan("PRO");
                      }
                    }}
                  >
                    <div className="register-plan-title-row">
                      <strong>Pro</strong>
                      <span className="register-plan-badge">
                        {PRO_BASE_PRICE_PER_USER_MONTH}/user/month
                      </span>
                    </div>
                    <p>
                      Ideal for growing teams with flexible billing periods.
                    </p>
                    <div className="register-duration-list">
                      {PRO_DURATION_OPTIONS.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          className={`register-duration-chip ${proDurationKey === option.key ? "selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setProDurationKey(option.key);
                            setSelectedPlan("PRO");
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <div className="register-plan-price">
                      {formatInr(pricing.total)}
                    </div>
                    <div className="register-plan-foot">
                      {pricing.effectiveDiscount > 0
                        ? `${pricing.effectiveDiscount}% duration discount applied`
                        : "No duration discount"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`register-plan-card ${selectedPlan === "ENTERPRISE" ? "selected" : ""}`}
                    onClick={() => setSelectedPlan("ENTERPRISE")}
                  >
                    <div className="register-plan-title-row">
                      <strong>Enterprise</strong>
                      <span className="register-plan-badge">Custom</span>
                    </div>
                    <p>
                      Need custom onboarding, security controls, and SLA
                      support?
                    </p>
                    <div className="register-plan-price">Contact Us</div>
                    <a
                      href="mailto:sales@decisionflow.in"
                      className="register-plan-contact-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Contact Sales
                    </a>
                  </button>
                </div>

                <div className="register-pricing-summary">
                  <h4>Payment Summary</h4>
                  {selectedPlan === "TRIAL" && (
                    <p>
                      Trial selected: Free for 14 days, up to 10 users.
                      {pricing.users > 10
                        ? " Reduce users to 10 or choose Pro/Enterprise."
                        : ""}
                    </p>
                  )}
                  {selectedPlan === "PRO" && (
                    <>
                      <p>
                        Pro plan for <strong>{pricing.users}</strong> users over{" "}
                        <strong>{selectedDuration.label}</strong>.
                      </p>
                      <p>Base Amount: {formatInr(pricing.gross)}</p>
                      <p>Discount: -{formatInr(pricing.discountAmount)}</p>
                      <p className="register-summary-total">
                        Total Payable: {formatInr(pricing.total)}
                      </p>
                      <p className="register-summary-note">
                        Effective per-user amount for this term:{" "}
                        {formatInr(pricing.perUserForTerm)}
                      </p>
                    </>
                  )}
                  {selectedPlan === "ENTERPRISE" && (
                    <p>
                      Enterprise selected: pricing and contract will be shared
                      by sales.
                    </p>
                  )}

                  {error && <div className="auth-alert">{error}</div>}

                  <div className="register-step2-actions">
                    <button
                      type="button"
                      className="auth-btn auth-btn-secondary"
                      disabled={loading}
                      onClick={() => {
                        setError("");
                        setStep(2);
                      }}
                    >
                      &larr; Back
                    </button>
                    <button
                      type="button"
                      className="auth-btn auth-btn-primary"
                      disabled={selectedPlan === "ENTERPRISE" || loading}
                      onClick={handlePrimaryAction}
                    >
                      {loading ? "Processing..." : actionButtonLabel}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          <p className="auth-copyright">© 2026 DecisionFlow. All rights reserved.</p>
        </div>
      </section>
      <aside className="auth-side">
        {step === 1 && (
          <>
            <h2>Launch With The Right Plan</h2>
            <p>
              Start with a 2-week trial, scale with Pro billing cycles, or talk
              to us for Enterprise rollout.
            </p>
          </>
        )}
        {step === 2 && (
          <>
            <h2>Pick the Module That Fits</h2>
            <p>
              Each module is purpose-built for a different type of business.
              You can only select one — choose the one that matches how your
              team actually works.
            </p>
          </>
        )}
        {step === 3 && (
          <>
            <h2>Simple, Transparent Pricing</h2>
            <p>
              Choose a plan that fits your team size. Upgrade or change your
              plan anytime as your company grows.
            </p>
          </>
        )}
      </aside>
    </div>
  );
}
